// Handwritten validation predicates for Quick Settings tunables (AC5, AC6, AC11).
//
// Pure functions: no DOM, no side effects, no Blockly mutation. The Apply batch (apply.js) calls
// these BEFORE any setFieldValue, so an invalid (empty / non-numeric / out-of-range) value never
// reaches the workspace — and, combined with the VALUE field-name guard + Events.disable() wrap,
// never triggers the tradeOptions cascade (AC16).
//
// NOTE on copy: AC6 (charter-audit) pins the empty-field error to the literal 'Field cannot be
// empty' — the spec deliberately couples the test to this string, so it is kept verbatim (not run
// through translate()). Free-form UI copy in the modal still uses translate().

const EMPTY_ERROR = 'Field cannot be empty';

/**
 * Validate a numeric tunable. Returns { ok, value, error }.
 * ok:false for empty / non-numeric / NaN / out-of-[min,max]. value is always a finite number on ok.
 */
export const validateNumber = (raw, { min, max } = {}) => {
    const text = raw === null || raw === undefined ? '' : String(raw).trim();
    if (text === '') {
        return { ok: false, error: EMPTY_ERROR };
    }
    const value = Number(text);
    if (Number.isNaN(value) || !Number.isFinite(value)) {
        return { ok: false, error: 'Must be a number' };
    }
    if (min !== undefined && min !== null && value < min) {
        return { ok: false, error: `Must be >= ${min}` };
    }
    if (max !== undefined && max !== null && value > max) {
        return { ok: false, error: `Must be <= ${max}` };
    }
    return { ok: true, value };
};

/**
 * Validate a text tunable. Returns { ok, value, error }.
 * ok:false (error 'Field cannot be empty') for empty / whitespace-only input.
 */
export const validateText = raw => {
    const text = raw === null || raw === undefined ? '' : String(raw).trim();
    if (text === '') {
        return { ok: false, error: EMPTY_ERROR };
    }
    return { ok: true, value: text };
};

/**
 * Re-read a block's VALUE/NUM field after a write to surface clamping (AC11).
 * Returns { stored, mismatch } where mismatch is true when the stored value differs from the
 * value the caller intended to write (e.g. a constrained FieldNumber clamped an out-of-range
 * input, or precision rounded it). Call before confirm so the user sees what was actually stored.
 */
export const readBack = (block, expectedValue, field = 'VALUE') => {
    const stored = block.getFieldValue(field);
    const mismatch = expectedValue !== undefined && String(stored) !== String(expectedValue);
    return { stored, mismatch };
};
